import React from 'react';
import { Inbox, Phone } from 'lucide-react';
import { Badge, Button, EmptyState, Table, Skeleton, SkeletonRegion } from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import Pager from '../../../components/common/Pager';
import { useMyPageStrings, hasLocalMyPageString } from '../myPageStrings';
import { LEADS_PAGE_SIZE } from '../MyPage.shared';

// ─────────────────────────────────────────────────────────────────────────────
// «درخواست‌های صفحه» — read-only (design §8.6). Working a request stays in the
// CRM queue; this tab only lets the doctor see who asked. One column
// definition drives the desktop table and the phone's cards.
// ─────────────────────────────────────────────────────────────────────────────

const LEAD_STATUS_BADGE = { new: 'default', contacted: 'outline', qualified: 'outline', won: 'success', lost: 'outline' };

function useLeadColumns() {
  const s = useMyPageStrings();
  const { formatDateTime, formatDigits } = useTranslation();
  // A CRM status this build does not know renders as the raw value, never a key.
  const statusText = (v) => (v && hasLocalMyPageString(`myPage.leads.statuses.${v}`) ? s(`myPage.leads.statuses.${v}`) : (v || '–'));
  return [
    { key: 'name', header: s('myPage.leads.name'), render: (r) => <bdi>{r.name || '–'}</bdi> },
    {
      key: 'mobile', header: s('myPage.leads.mobile'),
      render: (r) => (r.mobile ? (
        <a href={`tel:${r.mobile}`} dir="ltr" style={{ color: C.navy, unicodeBidi: 'isolate' }}>{formatDigits(r.mobile)}</a>
      ) : '–'),
    },
    {
      key: 'preference', header: s('myPage.leads.preference'),
      render: (r) => [
        r.preferredChannel ? s(`myPage.leads.channels.${r.preferredChannel}`) : null,
        r.preferredTime ? s(`myPage.leads.times.${r.preferredTime}`) : null,
      ].filter(Boolean).join(' · ') || '–',
    },
    { key: 'message', header: s('myPage.leads.message'), render: (r) => <span dir="auto">{r.message || '–'}</span> },
    { key: 'createdAt', header: s('myPage.leads.createdAt'), render: (r) => (r.createdAtMs ? formatDateTime(Number(r.createdAtMs)) : '–') },
    {
      key: 'status', header: s('myPage.leads.status'),
      render: (r) => <Badge variant={LEAD_STATUS_BADGE[r.status] || 'outline'}>{statusText(r.status)}</Badge>,
    },
  ];
}

function LeadsNotice({ published }) {
  const s = useMyPageStrings();
  return (
    <EmptyState
      data-testid="my-page-leads-empty"
      data-state="empty"
      icon={<Inbox size={44} strokeWidth={1.5} aria-hidden />}
      title={published ? s('myPage.leads.emptyTitle') : s('myPage.leads.notPublishedTitle')}
      description={published ? s('myPage.leads.emptyHint') : s('myPage.leads.notPublishedHint')}
    />
  );
}

export function LeadsTable({ leads, published }) {
  const s = useMyPageStrings();
  const columns = useLeadColumns();
  if (leads.error) {
    return <div data-testid="my-page-leads" data-state="error"><ErrorState error={leads.error} title={s('myPage.leads.loadFailed')} onRetry={leads.retry} /></div>;
  }
  return (
    <div data-testid="my-page-leads" data-state={leads.loading ? 'loading' : leads.items.length ? 'content' : 'empty'} style={{ display: 'grid', gap: '1rem' }}>
      <div style={{ fontSize: '0.8125rem', color: C.textSecondary }}>{s('myPage.leads.intro')}</div>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: RADIUS.card, overflow: 'hidden' }}>
        <Table
          columns={columns}
          rows={leads.items}
          loading={leads.loading && !leads.items.length}
          ariaLabel={s('myPage.tabs.leads')}
          empty={<LeadsNotice published={published} />}
          rowProps={(r) => ({ 'data-testid': `my-page-leads-row-${r.id}` })}
        />
      </div>
      <Pager
        page={leads.pageIndex + 1}
        pageCount={leads.pageCount}
        total={leads.total}
        pageSize={LEADS_PAGE_SIZE}
        onPage={(p) => leads.goTo(p - 1)}
      />
    </div>
  );
}

export function LeadCardsSkeleton() {
  const s = useMyPageStrings();
  return (
    <SkeletonRegion label={s('myPage.loading')} data-testid="my-page-leads-loading">
      {[0, 1, 2].map((i) => (
        <div key={i} aria-hidden style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: RADIUS.card, padding: '12px 14px', display: 'grid', gap: 8, marginBottom: 8 }}>
          <Skeleton width="45%" height={14} />
          <Skeleton width="30%" height={12} />
          <Skeleton width="70%" height={12} />
        </div>
      ))}
    </SkeletonRegion>
  );
}

export function LeadCards({ leads, published }) {
  const s = useMyPageStrings();
  const columns = useLeadColumns();
  const col = Object.fromEntries(columns.map((c) => [c.key, c]));
  if (leads.error) {
    return <div data-testid="my-page-leads" data-state="error"><ErrorState error={leads.error} title={s('myPage.leads.loadFailed')} onRetry={leads.retry} /></div>;
  }
  if (leads.loading && !leads.items.length) return <LeadCardsSkeleton />;
  if (!leads.items.length) return <div data-testid="my-page-leads" data-state="empty"><LeadsNotice published={published} /></div>;
  return (
    <div data-testid="my-page-leads" data-state="content" style={{ display: 'grid', gap: '0.5rem' }}>
      {leads.items.map((r) => (
        <article key={r.id} data-testid={`my-page-leads-row-${r.id}`} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: RADIUS.card, padding: '12px 14px', display: 'grid', gap: '0.375rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: '0.9375rem', color: C.textPrimary }}>{col.name.render(r)}</span>
            {col.status.render(r)}
          </div>
          {r.mobile ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem' }}>
              <Phone size={14} aria-hidden style={{ color: C.textSecondary }} />
              {col.mobile.render(r)}
            </div>
          ) : null}
          <div style={{ fontSize: '0.8125rem', color: C.textSecondary }}>{col.preference.render(r)}</div>
          {r.message ? <div style={{ fontSize: '0.8125rem', color: C.textPrimary }}>{col.message.render(r)}</div> : null}
          <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>{col.createdAt.render(r)}</div>
        </article>
      ))}
      {leads.hasMore ? (
        <Button variant="outline" onClick={leads.loadMore} disabled={leads.loading} data-testid="my-page-leads-more" className="min-h-11 w-full">
          {s('myPage.leads.more')}
        </Button>
      ) : null}
    </div>
  );
}
